import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { AssetAdd, AssetList, FeedbackAssetDraft, LoadingBar, releaseAssetPreview } from '@brafton/feedback-components';

import { ReporterIdentity } from '../identity-modal/identity-modal';

/** The fields collected by the draft popover (persisted by the host on submit). */
export interface NewFeedbackDraft {
  element: string;
  observation: string;
  /** The current screenshot canvas (with any annotations) as a PNG Blob. */
  screenshot: Blob | null;
  /** Extra assets the reporter attached: files to upload, and links. */
  assets: FeedbackAssetDraft[];
}

/**
 * Draft "new feedback" popover that opens beside a just-dropped marker. Shows the
 * captured screenshot on a <canvas> (with a Draw button overlay — wired later)
 * and collects element / observation (the API picks the category with AI). Emits
 * the draft on submit; the host owns persistence and the screenshot Blob.
 */
@Component({
  selector: 'app-new-feedback',
  templateUrl: './new-feedback.html',
  styleUrls: ['./new-feedback.scss', './new-feedback.premium.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingBar, AssetAdd, AssetList],
})
export class NewFeedback {
  /** Whether the popover is shown. */
  readonly open = input(false);

  /** True while the host is uploading + creating the feedback (shows a loading bar). */
  readonly submitting = input(false);

  /** The marker number this feedback is attached to. */
  readonly markerNumber = input(0);

  /** Object URL of the captured screenshot Blob, drawn onto the canvas. */
  readonly screenshotUrl = input<string | null>(null);

  /** Object URL of a recorded video Blob, embedded below the screenshot for review. */
  readonly videoUrl = input<string | null>(null);

  /**
   * Warning shown above the screenshot when the page uses features the in-page
   * capture may not render accurately (Elementor / parallax / cross-origin images).
   */
  readonly captureWarning = input<string | null>(null);

  /** The reporter chose to leave the screenshot out: the screenshot section is hidden. */
  readonly screenshotSkipped = input(false);

  /** The reporter (from the stored identity), shown in the header row. */
  readonly reporter = input<ReporterIdentity | null>(null);

  /** Marker position in viewport coordinates, used to place the popover beside it. */
  readonly anchorX = input(0);
  readonly anchorY = input(0);

  /** Emits the collected draft when the reporter submits. */
  readonly submit = output<NewFeedbackDraft>();

  /** Emits when the reporter dismisses the popover (Cancel / close / Esc). */
  readonly cancel = output<void>();

  protected readonly element = signal('');
  protected readonly observation = signal('');

  /** Extra assets attached with the "Add assets" button, listed below the screenshot. */
  protected readonly assets = signal<FeedbackAssetDraft[]>([]);

  /** Submit is enabled once the required fields (element + observation) are filled. */
  protected readonly canSubmit = computed(
    () => this.element().trim().length > 0 && this.observation().trim().length > 0,
  );

  /** Computed popover position (viewport coordinates), set after it renders. */
  protected readonly left = signal(0);
  protected readonly top = signal(0);

  /** Whether annotate/draw mode is active (form hidden, canvas enlarged). */
  protected readonly drawing = signal(false);

  /**
   * Object URL of a screenshot the reporter uploaded manually (the warning's
   * escape hatch). When set, it's painted onto the canvas INSTEAD of the
   * html2canvas capture, so the manual shot is what gets submitted.
   */
  protected readonly manualScreenshotUrl = signal<string | null>(null);

  /** True when a screenshot is present to show/submit (auto-captured or uploaded). */
  protected readonly hasScreenshot = computed(() => !!(this.manualScreenshotUrl() ?? this.screenshotUrl()));

  /** True while a pointer is down and actively drawing a stroke. */
  private strokeInProgress = false;

  private readonly popoverEl = viewChild<ElementRef<HTMLElement>>('popoverEl');
  private readonly shotCanvas = viewChild<ElementRef<HTMLCanvasElement>>('shotCanvas');

  constructor() {
    // Reset the form each time the popover opens; drop any manual upload + drawing
    // state when it closes (so a new draft doesn't reuse the last one's upload —
    // the screenshotUrl-keyed reset below won't fire for upload-only flows where
    // screenshotUrl stays null).
    effect(() => {
      if (this.open()) {
        this.element.set('');
        this.observation.set('');
      } else {
        this.drawing.set(false);
        untracked(() => {
          this.clearManualScreenshot();
          this.clearAssets();
        });
      }
    });

    // Place the popover beside the marker once it has rendered (so we can measure
    // it). Re-runs when `drawing` toggles, since the popover widens for drawing.
    effect(() => {
      if (!this.open()) {
        return;
      }
      const anchorX = this.anchorX();
      const anchorY = this.anchorY();
      this.drawing();
      requestAnimationFrame(() => this.positionBesideMarker(anchorX, anchorY));
    });

    // A fresh capture (new screenshotUrl from the host) discards any manual upload
    // from a previous draft, so the popover doesn't reuse a stale manual shot.
    effect(() => {
      this.screenshotUrl();
      untracked(() => this.clearManualScreenshot());
    });

    // Paint the screenshot onto the canvas — the manual upload takes priority over
    // the html2canvas capture. The backing store matches the image's TRUE dimensions
    // so the full viewport is preserved (no cover-crop) in both the preview and the
    // uploaded blob. CSS scales the canvas down to fit the popover; the pointer→canvas
    // mapping stays linear because the element keeps the canvas's aspect ratio.
    effect(() => {
      const url = this.manualScreenshotUrl() ?? this.screenshotUrl();
      const canvas = this.shotCanvas()?.nativeElement;
      if (!this.open() || !url || !canvas) {
        return;
      }
      const image = new Image();
      image.onload = () => {
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext('2d');
        if (!context) {
          return;
        }
        context.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight);
      };
      image.src = url;
    });
  }

  /** Replaces the canvas screenshot with one the reporter picked from their device. */
  protected onUploadScreenshot(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Reset the input so the same file can be re-selected later.
    input.value = '';
    if (!file || !file.type.startsWith('image/')) {
      return;
    }
    const previous = this.manualScreenshotUrl();
    if (previous) {
      URL.revokeObjectURL(previous);
    }
    // The paint effect repaints the canvas from this URL (backing store sized to the
    // uploaded image), so the manual shot becomes what's submitted.
    this.manualScreenshotUrl.set(URL.createObjectURL(file));
  }

  /** Drops the manual upload (revoking its object URL) so the capture is used again. */
  protected onAssetsAdded(addedAssets: FeedbackAssetDraft[]): void {
    this.assets.update((currentAssets) => [...currentAssets, ...addedAssets]);
  }

  protected onAssetRemoved(removedAsset: FeedbackAssetDraft): void {
    releaseAssetPreview(removedAsset);
    this.assets.update((currentAssets) => currentAssets.filter((asset) => asset.id !== removedAsset.id));
  }

  /** Drops the draft's assets and frees their image thumbnails. */
  private clearAssets(): void {
    this.assets().forEach(releaseAssetPreview);
    this.assets.set([]);
  }

  private clearManualScreenshot(): void {
    const previous = this.manualScreenshotUrl();
    if (previous) {
      URL.revokeObjectURL(previous);
      this.manualScreenshotUrl.set(null);
    }
  }

  /** Toggles annotate/draw mode. */
  protected toggleDraw(): void {
    this.drawing.update((on) => !on);
  }

  /** Begins a red free-hand stroke on the screenshot canvas. */
  protected onCanvasPointerDown(event: PointerEvent): void {
    if (!this.drawing()) {
      return;
    }
    const canvas = this.shotCanvas()?.nativeElement;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) {
      return;
    }
    this.strokeInProgress = true;
    canvas.setPointerCapture(event.pointerId);
    const point = this.toCanvasPoint(canvas, event);
    context.strokeStyle = '#ef3b4d';
    context.lineWidth = canvas.width * 0.006;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.beginPath();
    context.moveTo(point.x, point.y);
  }

  /** Extends the current stroke as the pointer moves. */
  protected onCanvasPointerMove(event: PointerEvent): void {
    if (!this.strokeInProgress) {
      return;
    }
    const canvas = this.shotCanvas()?.nativeElement;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) {
      return;
    }
    const point = this.toCanvasPoint(canvas, event);
    context.lineTo(point.x, point.y);
    context.stroke();
  }

  /** Ends the current stroke. */
  protected onCanvasPointerUp(): void {
    this.strokeInProgress = false;
  }

  /** Maps a pointer event to canvas backing-store coordinates (linear, 16:9 box). */
  private toCanvasPoint(canvas: HTMLCanvasElement, event: PointerEvent): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
    };
  }

  protected async onSubmit(): Promise<void> {
    if (!this.canSubmit() || this.submitting()) {
      return;
    }
    // Emit the CURRENT canvas state (poster + any red annotations) as the screenshot.
    // When no screenshot was captured or uploaded, the canvas is blank — send null
    // rather than an empty image.
    const screenshot = this.hasScreenshot() ? await this.canvasToBlob() : null;
    this.submit.emit({
      element: this.element().trim(),
      observation: this.observation().trim(),
      screenshot,
      assets: this.assets(),
    });
  }

  /** Snapshots the screenshot canvas (with annotations) to a PNG Blob. */
  private canvasToBlob(): Promise<Blob | null> {
    const canvas = this.shotCanvas()?.nativeElement;
    if (!canvas) {
      return Promise.resolve(null);
    }
    return new Promise<Blob | null>((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/png'));
  }

  /**
   * Positions the popover to the right of the marker (flips left if it would
   * overflow), clamped to the viewport. Reads the rendered popover's size.
   */
  private positionBesideMarker(anchorX: number, anchorY: number): void {
    const element = this.popoverEl()?.nativeElement;
    const width = element?.offsetWidth ?? 380;
    const height = element?.offsetHeight ?? 0;
    const gap = 24;

    let left = anchorX + gap;
    if (left + width > window.innerWidth - 16) {
      left = anchorX - width - gap;
    }
    left = Math.max(16, left);

    const top = Math.max(16, Math.min(anchorY - 40, window.innerHeight - height - 16));

    this.left.set(left);
    this.top.set(top);
  }
}
